import type { RouterOutputs } from '~/lib/trpc';

/** One transcribed token, as the server stores it. */
export type Word = NonNullable<
  RouterOutputs['calls']['transcription']['getCallTranscriptWords']
>[number];

/** A token and where it sits in the stream, which is what the playhead seeks by. */
export type TurnToken = Word & { index: number };

export type Turn = {
  speaker: string;
  startSecond: number;
  tokens: Array<TurnToken>;
};

/** Who was on a channel, and when, in seconds from the start of the recording. */
export type SpeakerSpan = {
  channel: number;
  from: number;
  to: number;
  kind: 'client' | 'reception' | 'ai' | 'unknown';
  name: string | null;
};

/** A turn is opened by somebody speaking: blank tokens and audio events cannot
 *  start one, and an audio event is in no turn at all. */
export function toTurns(words: Array<Word>): Array<Turn> {
  const turns: Array<Turn> = [];

  words.forEach((word, index) => {
    if (word.type === 'audio_event') {
      return;
    }

    const last = turns.at(-1);

    if (last?.speaker === word.speaker) {
      last.tokens.push({ ...word, index });
      return;
    }

    if (word.text.trim().length === 0) {
      return;
    }

    turns.push({
      speaker: word.speaker,
      startSecond: word.startSecond,
      tokens: [{ ...word, index }],
    });
  });

  return turns;
}

/** Null for a speaker id that does not name an audio channel. */
export function channelOf(speaker: string): number | null {
  const match = /^channel_(\d+)$/.exec(speaker);

  return match?.[1] === undefined ? null : Number(match[1]);
}

/** A turn with no span is one we cannot place. */
export function speakerAt(
  timeline: ReadonlyArray<SpeakerSpan>,
  channel: number,
  second: number,
): SpeakerSpan | null {
  return (
    timeline.find(
      (span) =>
        span.channel === channel && second >= span.from && second < span.to,
    ) ?? null
  );
}
